import { type ComputedRef, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCan } from "@/features/auth/api/can";
import { useComponentScreens } from "@/features/component-views/api/use-component-view";
import { usePlatformAccess } from "@/features/platform/api/use-platform";
import { useStructure } from "@/features/structure/api/use-structure";
import { useMyAccess } from "@/shared/access/use-access";
import type { Destination } from "@/shared/contracts/destinations";
import { paletteScopeMessages } from "./i18n/palette";
import { NAV_ITEMS, SYSTEM_SECTIONS, visibleItems } from "./nav-items";

/**
 * As telas desta sessão, numa lista só — a MESMA que o ⌘K desenha.
 *
 * `visibleItems` e não um filtro escrito de novo: oferecer ao agente um destino
 * que a pessoa não pode abrir é oferecer uma porta que 403. E as entidades saem
 * da estrutura viva, nunca de um array — é o que faz uma tela criada de tarde
 * ser navegável de tarde, sem deploy do front (F-ADR-009).
 *
 * As três consultas já estão em voo: a paleta, montada na moldura em toda tela,
 * lê as mesmas três. O `vue-query` responde do cache.
 */
export function useDestinations(enabled: () => boolean = () => true): ComputedRef<Destination[]> {
	// O agente oferece a tela pelo nome que a PESSOA vê — um destino ditado em
	// inglês num painel em português seria um lugar que ela não encontra no menu.
	const { t } = useI18n({
		useScope: "local",
		inheritLocale: true,
		// O MESMO dicionário da paleta (`paletteScopeMessages`, que já junta menu,
		// paleta e Configurações): as telas nomeiam-se pelo menu e as abas com
		// endereço próprio pelo da paleta, que é onde elas já são nomeadas. Um
		// terceiro dicionário só para o agente seria a lista de nomes que
		// envelhece sozinha.
		messages: paletteScopeMessages,
	});
	const { data: entities } = useStructure(enabled);
	const { data: screens } = useComponentScreens(enabled);
	const { data: platformAdmin } = usePlatformAccess(enabled);
	const isAdmin = useCan("admin");
	// Com o mesmo `enabled` das vizinhas: era a única consulta daqui que corria
	// numa página pública (ver `useMyAccess`).
	const { data: moduleAccess } = useMyAccess(enabled);

	return computed<Destination[]>(() => [
		...visibleItems(NAV_ITEMS, {
			isAdmin: isAdmin.value,
			disabledModules: moduleAccess.value?.disabledModules,
			platformAdmin: platformAdmin.value === true,
		}).map((item) => ({ to: item.to, label: t(item.labelKey), hint: t(item.hintKey) })),
		/**
		 * AS ABAS COM ENDEREÇO PRÓPRIO — e só para quem as pode abrir.
		 *
		 * O agente só navega para um destino desta lista, letra por letra
		 * (`navigation.tools.ts`): sem elas, ele sabia dizer "liga em O que pode
		 * ter" e não sabia levar ninguém lá.
		 */
		...(isAdmin.value && !moduleAccess.value?.disabledModules?.includes("system")
			? SYSTEM_SECTIONS.map((section) => ({
					to: section.to,
					label: t(section.labelKey),
					hint: t(section.hintKey),
				}))
			: []),
		...(entities.value ?? [])
			.filter((entity) => entity.status === "active")
			.map((entity) => ({ to: `/e/${entity.code}`, label: entity.name, hint: entity.code })),
		// As telas do construtor, ditáveis pelo nome como qualquer outro destino.
		...(screens.value ?? []).map((screen) => ({
			to: `/t/${screen.entityCode}/${screen.code}`,
			label: screen.name,
			hint: screen.entityName,
		})),
	]);
}
